import { Vector2 } from "../../../common/vector2.js";
import { IController, StopBehavior } from "../types/controller_types.js";

export interface IEmitterConfig
{
  rate_over_time      ?: number;
  rate_over_distance  ?: number;
  count               ?: number;
}

export class Emitter implements IController
{
  private static next_id: number = 1;
  public readonly id: number = Emitter.next_id++;

  /** Встановлюється батчем після addEmitter(). Його викликає stop() якщо behavior !== KeepParticles. */
  public onStop?: ( behavior: StopBehavior, fade_duration: number ) => void;

  private _is_active: boolean = true;
  public get is_active(): boolean { return this._is_active; }

  private _position: Vector2;
  public get position(): Vector2 { return this._position; }
  
  private _start_time: number;
  public get start_time(): number { return this._start_time; }

  private _duration: number | null = null;
  public get duration(): number | null { return this._duration; }

  private count: number;
  private readonly time_step: number = 0;
  private readonly dist_step: number = 0;

  private last_emit_time : number;
  private last_emit_pos  : Vector2 | null = null;
  private remainder_dist : number = 0;

  private sub_controllers: IController[] = [];

  constructor( position: Vector2, start_time: number, duration?: number, config?: IEmitterConfig )
  {
    this._position = position.clone();
    this._start_time = start_time;
    this.last_emit_time = start_time;
    
    if ( duration !== undefined )
      this._duration = duration;

    this.count = config?.count ?? 1;

    if ( config?.rate_over_time && config.rate_over_time > 0 )
      this.time_step = 1000 / config.rate_over_time; 

    if ( config?.rate_over_distance && config.rate_over_distance > 0 )
      this.dist_step = 1.0 / config.rate_over_distance;

    this.last_emit_time = start_time - this.time_step;
  }

  public addSubController( controller: IController ): void
  {
    this.sub_controllers.push( controller );
  }

  public setPosition( pos: Vector2 ): void
  {
    this._position.copyFrom( pos );
    for ( const sub of this.sub_controllers )
      sub.setPosition( pos );
  }

  public accelerate( factor: number, now: number ): void
  {
    const elapsed = now - this._start_time;
    
    // Прискорюємо тривалість життя емітера
    if ( this._duration !== null )
    {
      const remaining = this._duration - elapsed;
      if ( remaining > 0 )
      {
        this._start_time = now - elapsed * factor;
        this._duration = ( elapsed + remaining ) / factor;
      }
    }
    else
    {
      // Якщо duration нескінченний, просто зміщуємо точку відліку, щоб зберегти фазу
      this._start_time = now - elapsed * factor;
    }

    // Прискорюємо внутрішній годинник спавну (time_step)
    if ( this.time_step > 0 )
    {
      const time_since_last = now - this.last_emit_time;
      // Встановлюємо новий час останнього спавну так, щоб зберегти прогрес до наступної частинки
      this.last_emit_time = now - time_since_last * factor;
      // time_step не змінюємо, бо він використовується в process(now), 
      // але ми змінили last_emit_time, що вплине на розрахунок у наступному кадрі.
      // Насправді, нам ТРЕБА змінити сам time_step, якщо ми хочемо, щоб він і далі спавнив швидше.
      // АЛЕ: у нас rate_over_time фіксований у конфигу.
      // Правильніше — прискорити саме "плин часу" для емітера.
    }

    for ( const sub of this.sub_controllers )
      sub.accelerate( factor, now );
  }

  public process( now: number, emitCallback: ( spawn_pos: Vector2, spawn_time: number ) => void ): void
  {
    if ( now < this._start_time )
      return;

    if ( this.time_step > 0 )
    {
      let time_passed = now - this.last_emit_time;
      if ( time_passed > 1000 )
      {
        time_passed = 1000;
        this.last_emit_time = now - 1000;
      }

      while ( time_passed >= this.time_step )
      {
        this.last_emit_time += this.time_step;
        time_passed -= this.time_step;

        for ( let c = 0; c < this.count; c++ )
          emitCallback( this._position, this.last_emit_time );
      }
    }

    if ( this.dist_step > 0 )
    {
      if ( !this.last_emit_pos )
        this.last_emit_pos = this._position.clone();
      else
      {
        const dx = this._position.x - this.last_emit_pos.x;
        const dy = this._position.y - this.last_emit_pos.y;
        const dist = Math.sqrt( dx * dx + dy * dy );

        if ( dist > 0.0001 )
        {
          const total_dist = this.remainder_dist + dist;
          const spawn_count = Math.floor( total_dist / this.dist_step );

          if ( spawn_count > 0 )
          {
            const step_t = this.dist_step / dist;
            let current_t = ( this.dist_step - this.remainder_dist ) / dist;

            for ( let i = 0; i < spawn_count; i++ )
            {
              const spawn_x = this.last_emit_pos.x + dx * current_t;
              const spawn_y = this.last_emit_pos.y + dy * current_t;
              
              const spawn_pos = new Vector2( spawn_x, spawn_y );

              for ( let c = 0; c < this.count; c++ )
                emitCallback( spawn_pos, now );
                
              current_t += step_t;
            }
            this.remainder_dist = total_dist - spawn_count * this.dist_step;
          }
          else
            this.remainder_dist = total_dist;

          this.last_emit_pos.copyFrom( this._position );
        }
      }
    }
  }

  public getProgress( now: number ): number
  {
    if ( this._duration === null )
      return 0;

    if ( this._duration <= 0 )
      return 1;

    const age = now - this._start_time;
    return Math.max( 0, Math.min( 1, age / this._duration ) );
  }

  public stop( behavior: StopBehavior = StopBehavior.KeepInstances, fade_duration: number = 0 ): void
  {
    if ( !this._is_active )
      return;
    this._is_active = false;

    if ( behavior !== StopBehavior.KeepInstances )
      this.onStop?.( behavior, fade_duration );

    for ( const sub of this.sub_controllers )
      sub.stop( behavior, fade_duration );
  }
}